/* global __PC_BUILD_ID__ */

const RELOAD_TARGET_KEY = "pc_app_reload_target";

export const CURRENT_APP_BUILD_ID = typeof __PC_BUILD_ID__ === "string"
  ? __PC_BUILD_ID__
  : "unversioned";

export function isStandaloneDisplay(windowObj = window, navigatorObj = navigator) {
  return Boolean(
    navigatorObj?.standalone
    || windowObj?.matchMedia?.("(display-mode: standalone)")?.matches,
  );
}

export async function checkForAppUpdate(overrides = {}) {
  const fetchImpl = overrides.fetchImpl || window.fetch.bind(window);
  const storage = overrides.storage || window.sessionStorage;
  const reload = overrides.reload || (() => window.location.reload());
  const currentBuildId = overrides.currentBuildId || CURRENT_APP_BUILD_ID;
  const now = overrides.now || Date.now;
  const response = await fetchImpl(`/app-version.json?t=${now()}`, {
    cache: "no-store",
    credentials: "same-origin",
  });
  if (!response.ok) return { status: "unavailable" };
  const latestBuildId = String((await response.json())?.build_id || "").trim();
  if (!latestBuildId) return { status: "unavailable" };
  if (latestBuildId === currentBuildId) {
    if (storage.getItem(RELOAD_TARGET_KEY) === latestBuildId) storage.removeItem(RELOAD_TARGET_KEY);
    return { status: "current", buildId: latestBuildId };
  }
  if (storage.getItem(RELOAD_TARGET_KEY) === latestBuildId) {
    return { status: "reload_guarded", buildId: latestBuildId };
  }
  storage.setItem(RELOAD_TARGET_KEY, latestBuildId);
  reload();
  return { status: "reloading", buildId: latestBuildId };
}

export function startAppVersionMonitor(overrides = {}) {
  const windowObj = overrides.windowObj || window;
  const documentObj = overrides.documentObj || document;
  const navigatorObj = overrides.navigatorObj || navigator;
  documentObj.documentElement.dataset.pcBuildId = CURRENT_APP_BUILD_ID;
  documentObj.documentElement.dataset.pcDisplayMode = isStandaloneDisplay(windowObj, navigatorObj)
    ? "standalone"
    : "browser";
  let pendingCheck = null;
  const check = () => {
    if (!pendingCheck) {
      pendingCheck = checkForAppUpdate(overrides)
        .catch(() => ({ status: "unavailable" }))
        .finally(() => { pendingCheck = null; });
    }
    return pendingCheck;
  };
  const onVisibility = () => { if (documentObj.visibilityState !== "hidden") check(); };
  windowObj.addEventListener("pageshow", check);
  windowObj.addEventListener("focus", check);
  documentObj.addEventListener("visibilitychange", onVisibility);
  check();
  return () => {
    windowObj.removeEventListener("pageshow", check);
    windowObj.removeEventListener("focus", check);
    documentObj.removeEventListener("visibilitychange", onVisibility);
  };
}
